import { MapPosition } from '@innovyze/inno-map';
import { DataServiceType } from '@innovyze/inno-map/map/services/types';

import { assetsBaseUrl, getTilesBaseUrl, tilesBaseUrl } from '@Apis';
import { SourceOptions, GeneralOptions, MapOptions, MapLocation } from '@Types';
import { Extent } from '@innovyze/shared-utils';

const queryExtent = (
	opts: GeneralOptions,
	extent: Extent | undefined,
): string =>
	opts.useBBox && extent && extent.length === 4
		? `&bbox=${extent[0].toFixed(opts.precisionLonLat)}` +
		  `,${extent[1].toFixed(opts.precisionLonLat)}` +
		  `,${extent[2].toFixed(opts.precisionLonLat)}` +
		  `,${extent[3].toFixed(opts.precisionLonLat)}`
		: '';

const queryLLR = (
	opts: GeneralOptions,
	position: MapPosition | undefined,
	radius: number | undefined,
): string =>
	position && radius
		? `&lon=${position.center[0].toFixed(opts.precisionLonLat)}` +
		  `&lat=${position.center[1].toFixed(opts.precisionLonLat)}` +
		  `&rad=${radius.toFixed(opts.precisionRadius)}`
		: '';

const queryOthers = (opts: GeneralOptions): string =>
	opts.singlePage ? '&singlePage=1' : '';

const query = (opts: GeneralOptions, location: MapLocation): string =>
	queryLLR(opts, location.position, location.radius) +
	queryExtent(opts, location.extent) +
	queryOthers(opts);

const url = (
	source: SourceOptions,
	opts: GeneralOptions,
	location: MapLocation,
): string => `${source.url}?${query(opts, location).slice(1)}`;

const urlWithInspectionId = (
	source: SourceOptions,
	inspectionId: string,
): string => `${source.url}/${inspectionId}`;

export const dataServices = (
	token: string | undefined,
	options: MapOptions,
	location?: MapLocation,
	inspectionId?: string,
	isCloudOsTilesetEnabled = false,
): DataServiceType<unknown>[] => {
	if (!token) {
		return [];
	}

	// Resolve the tiles host from the live flag rather than trusting the
	// baked-in `.url` (frozen from tileOptions at import time, before flags
	// existed). Flag off returns the identical legacy host.
	const tilesUrl = getTilesBaseUrl(isCloudOsTilesetEnabled);

	const sources: DataServiceType<unknown>[] = [];

	// Only include tilesSource if it exists and is not hidden
	if (options.tilesSource && !options.tilesSourceHide) {
		sources.push({
			displayName: options.tilesSource.layerName,
			id: options.tilesSource.dataServiceId,
			type: options.tilesSource.type,
			url: tilesUrl,
			token: token,
			propsUrl: {
				any: `${assetsBaseUrl}assets/{{assetId}}?assetId={{assetId}}&assetType={{assetType}}`,
				sensor: `${assetsBaseUrl}sensors/{{assetId}}`,
			},
			// Required for the INFO360_TILES_V2 adaptor to wire up the
			// per-feature vector-tile renderer; without it, tiles load but
			// no features are drawn on inset maps (asset / inspection /
			// spatial detail).
			attributesUrl: assetsBaseUrl,
			// No propertyLookup for inset maps. Inset maps render the 4 base
			// composite layers (water/sewer × pipe/manhole) — no themed
			// variants reference a propertyLookup. Including one here makes
			// useLayers' pendingLookupsRef gate block all layer rendering
			// until the lookup request resolves (~4s for risk/score), which
			// manifests as a blank inset map. Keep this propertyLookup empty
			// unless adding inset variants that need it.
		});
	}

	if (options.inspectionSources) {
		if (inspectionId) {
			// When inspectionId is provided, use it instead of location-based query
			options.inspectionSources.forEach(s => {
				sources.push({
					displayName: s.layerName,
					id: s.dataServiceId,
					type: s.type,
					url: urlWithInspectionId(s, inspectionId),
					token: token,
				});
			});
		} else if (location && location.extent) {
			// Fall back to location-based query when no inspectionId is provided
			options.inspectionSources.forEach(s => {
				sources.push({
					displayName: s.layerName,
					id: s.dataServiceId,
					type: s.type,
					url: url(s, options.generalOptions, location),
					token: token,
				});
			});
		}
	}

	if (options.otherSource) {
		sources.push({
			displayName: options.otherSource.layerName,
			id: options.otherSource.dataServiceId,
			type: options.otherSource.type,
			// Swap the legacy tiles base for the flag-resolved host while
			// preserving whatever tile-json path follows. No-op when the flag
			// is off (tilesUrl === tilesBaseUrl).
			url: options.otherSource.url.replace(tilesBaseUrl, tilesUrl),
			token: token,
		});
	}

	return sources;
};
